import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Button,
  Box,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  confirmationBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '1rem',
    borderRadius: '4px',
  },
  confirmationText: {
    margin: 'auto',
    textAlign: 'center',
    fontSize: '1.1rem',
  },
}));

interface PolicyData {
  uploadDeadline: string;
  file: File | null;
}

interface PolicyUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  policyData: PolicyData;
  setPolicyData: (data: PolicyData) => void;
  loading: boolean;
}

const PolicyUploadModal = ({
  open,
  onClose,
  onSubmit,
  policyData,
  setPolicyData,
  loading,
}: PolicyUploadModalProps) => {
  const classes = useStyles();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setPolicyData({
      uploadDeadline: '',
      file: null,
    });
    onClose();
  };

  const handleContinue = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    onSubmit();
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Typography variant='h4'>
          {showConfirmation ? 'Confirmación de operación' : 'Carga de póliza'}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {!showConfirmation ? (
          <>
            <TextField
              variant='outlined'
              label='Fecha de vencimiento'
              type='date'
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={policyData.uploadDeadline}
              onChange={(e) =>
                setPolicyData({
                  ...policyData,
                  uploadDeadline: e.target.value,
                })
              }
            />
            {policyData.file && (
              <Typography variant='body2'>
                Archivo seleccionado: {policyData.file.name}
              </Typography>
            )}
            <Button
              color='secondary'
              variant='contained'
              component='label'
              startIcon={<CloudUploadIcon />}
              style={{ width: 'fit-content', margin: 'auto' }}
              disabled={loading || !policyData.uploadDeadline}
            >
              Seleccionar archivo
              <input
                type='file'
                hidden
                onChange={(e) =>
                  setPolicyData({
                    ...policyData,
                    file: e.target.files?.[0] || null,
                  })
                }
              />
            </Button>
          </>
        ) : (
          <Box className={classes.confirmationBox}>
            <Typography variant='h6' className={classes.confirmationText}>
              Se asignará la siguiente fecha de vencimiento: <br />{' '}
              {formatDate(policyData.uploadDeadline)}
            </Typography>
            <Typography variant='body2' className={classes.confirmationText}>
              Al documento: <br />{' '} {policyData.file?.name}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          Cancelar
        </Button>
        {!showConfirmation ? (
          <Button
            onClick={handleContinue}
            color='primary'
            disabled={
              loading ||
              !policyData.uploadDeadline ||
              !policyData.file
            }
          >
            Continuar
          </Button>
        ) : (
          <>
            <Button onClick={handleCancel} color='primary'>
              Volver
            </Button>
            <Button onClick={handleConfirm} color='primary' disabled={loading}>
              Confirmar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PolicyUploadModal;
